import { Pow18 } from 'config'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { updateCreateOrderOption } from 'store/spot/actions'
import {
  useCreateOrderOption,
  useCurrentSpotSymbol,
  useLastInputAmountType,
} from 'store/spot/hooks'
import { LastInputAmountType } from 'store/spot/types'
import { bn, e2w, isZero, toFixed, w2e } from 'utils/number'
import { orderPercentAtom } from '../../views/Spot/CreateOrder/AmountPercent/atoms'
import { useSetAtom } from 'jotai'

export const useLimitPriceCallback = () => {
  const dispatch = useDispatch()
  const lastInputAmountType = useLastInputAmountType()
  const currentSpotSymbol = useCurrentSpotSymbol()
  const { baseScale = 0, quoteScale = 0 } = currentSpotSymbol
  const { quoteCurrencyAmount, baseCurrencyAmount } = useCreateOrderOption()
  const setOrderPercent = useSetAtom(orderPercentAtom)

  return useMemo(() => {
    return {
      onLimitPriceInput: async (price: string) => {
        try {
          if (+price <= 0) {
            dispatch(
              updateCreateOrderOption({
                option: 'quoteCurrencyAmount',
                value: '0',
              }),
            )
          }
          const safePrice = toFixed(price, quoteScale)
          dispatch(
            updateCreateOrderOption({ option: 'limitPrice', value: safePrice }),
          )
          if (lastInputAmountType === LastInputAmountType.BaseCurrency) {
            if (baseCurrencyAmount && safePrice) {
              const quoteWei = bn(e2w(baseCurrencyAmount))
                .mul(e2w(safePrice))
                .div(Pow18)
              dispatch(
                updateCreateOrderOption({
                  option: 'quoteCurrencyAmount',
                  value: toFixed(w2e(quoteWei), quoteScale),
                }),
              )
            }
          } else if (
            lastInputAmountType === LastInputAmountType.QuoteCurrency
          ) {
            if (!isZero(quoteCurrencyAmount) && !isZero(safePrice)) {
              const baseWei = e2w(quoteCurrencyAmount)
                .mul(Pow18)
                .div(e2w(safePrice))
              dispatch(
                updateCreateOrderOption({
                  option: 'baseCurrencyAmount',
                  value: toFixed(w2e(baseWei), baseScale),
                }),
              )
            }
          }
          // Reset order percent slider
          setOrderPercent(0)
          return Promise.resolve()
        } catch (error) {
          return Promise.reject(error)
        }
      },
    }
  }, [
    lastInputAmountType,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    currentSpotSymbol,
  ])
}
